import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { roleAllows, useAuth } from "@/lib/auth";
import PanelState, { extractApiError } from "@/components/PanelState";
import { useParams } from "react-router-dom";
import { Users, Save, ShieldAlert, Loader2, RefreshCw, Mail, Clock, Briefcase, Link2, Trash2, Plus, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import CallPrepSettingsTab from "./hiring/CallPrepSettingsTab";

// ============================================================================
// Hiring Settings — Phase A UI (2026-08-21 pt.15).
// Three sections: Roles (dynamic assignment), SLA/Business hours, Templates.
// Backed by /api/hiring/roles, /api/hiring/settings, /api/hiring/templates.
// ============================================================================

const ROLE_META = {
  stage_1_approver:        { label: "Stage 1 — Approver",          hint: "Approves candidates coming out of intake. Solo owner.",         maxPrimary: 1, spec: "Missie Bechard" },
  stage_2_outreach_owner:  { label: "Stage 2 — Outreach Owner",    hint: "Runs the intake call. Solo owner.",                             maxPrimary: 1, spec: "Juliana Toms" },
  stage_3_interviewer:     { label: "Stage 3 — Interviewer",       hint: "Conducts the technical/culture interview. Solo owner.",         maxPrimary: 1, spec: "James Pederson" },
  stage_4_interviewers:    { label: "Stage 4 — Owner/COO (go/no-go decision)", hint: "Fixed dual-owner. Approves the staff meeting (go) or declines. No reassignment or backup.",                  maxPrimary: 2, spec: "Brett Payne + Nathanael Toms" },
  stage_4_meeting_offer_executor: { label: "Stage 5 — Meeting/Offer Executor", hint: "Owns Stage 5, which starts only after the Stage 4 Owner/COO approves the staff meeting. Sends the invite + runs the Extend offer flow. Solo owner.", maxPrimary: 1, spec: "Juliana Toms" },
};

const TEMPLATE_META = {
  post_stage_1_decline:         { label: "Warm decline — post Stage 1 (hard-gate fail)", tone: "decline" },
  post_stage_2_decline:         { label: "Warm decline — post Stage 2 (intake call)",     tone: "decline" },
  intake_link_to_candidate:     { label: "Intake link to candidate",                       tone: "candidate" },
  stage_1_notify_approver:      { label: "Stage 1 — notify approver",                      tone: "notify" },
  stage_2_notify_outreach_owner:{ label: "Stage 2 — notify outreach owner",                tone: "notify" },
  stage_3_notify_interviewer:   { label: "Stage 3 — notify interviewer",                   tone: "notify" },
  stage_4_notify_interviewers:  { label: "Stage 4 — notify interviewers",                  tone: "notify" },
  stage_5_offer:                { label: "Stage 5 — offer",                                tone: "candidate" },
  tier_1_escalation_stage_1:    { label: "SLA — Tier 1 escalation (Stage 1)",              tone: "sla" },
  tier_2_reassignment_notice:   { label: "SLA — Tier 2 reassignment notice",               tone: "sla" },
};

export default function HiringSettings() {
  const { user } = useAuth();
  const { slug } = useParams();
  const [tab, setTab] = useState("roles");
  const [state, setState] = useState({ loading: true, error: null });
  const [roles, setRoles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [postings, setPostings] = useState([]);
  const [saveState, setSaveState] = useState({});

  const canWrite = useMemo(
    () => user && roleAllows(user.role, ["platform_admin", "owner"]),  // CC-261
    [user],
  );

  async function load() {
    setState({ loading: true, error: null });
    try {
      const q = `?tenant_slug=${encodeURIComponent(slug)}`;
      const [r1, r2, r3, r4] = await Promise.all([
        api.get(`/hiring/roles${q}`),
        api.get(`/hiring/settings${q}`),
        api.get(`/hiring/templates${q}`),
        api.get(`/hiring/postings${q}`),
      ]);
      setRoles(r1.data.roles || []);
      setSettings(r2.data);
      setTemplates(r3.data.templates || []);
      setPostings(r4.data.postings || []);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: extractApiError(e) });
    }
  }

  useEffect(() => { load(); }, [slug]);

  async function saveRole(role_key, patch) {
    setSaveState((s) => ({ ...s, [role_key]: { saving: true } }));
    try {
      const q = `?tenant_slug=${encodeURIComponent(slug)}`;
      const { data } = await api.patch(`/hiring/roles/${role_key}${q}`, patch);
      setRoles((rs) => rs.map((r) => (r.role_key === role_key ? data : r)));
      setSaveState((s) => ({ ...s, [role_key]: { saved: true } }));
      setTimeout(() => setSaveState((s) => ({ ...s, [role_key]: {} })), 2000);
    } catch (e) {
      setSaveState((s) => ({ ...s, [role_key]: { error: extractApiError(e) } }));
    }
  }

  async function saveSettings(patch) {
    setSaveState((s) => ({ ...s, settings: { saving: true } }));
    try {
      const q = `?tenant_slug=${encodeURIComponent(slug)}`;
      const { data } = await api.patch(`/hiring/settings${q}`, patch);
      setSettings(data);
      setSaveState((s) => ({ ...s, settings: { saved: true } }));
      setTimeout(() => setSaveState((s) => ({ ...s, settings: {} })), 2000);
    } catch (e) {
      setSaveState((s) => ({ ...s, settings: { error: extractApiError(e) } }));
    }
  }

  async function saveTemplate(template_key, patch) {
    setSaveState((s) => ({ ...s, [template_key]: { saving: true } }));
    try {
      const q = `?tenant_slug=${encodeURIComponent(slug)}`;
      const { data } = await api.patch(`/hiring/templates/${template_key}${q}`, patch);
      setTemplates((ts) => ts.map((t) => (t.template_key === template_key ? data : t)));
      setSaveState((s) => ({ ...s, [template_key]: { saved: true } }));
      setTimeout(() => setSaveState((s) => ({ ...s, [template_key]: {} })), 2000);
    } catch (e) {
      setSaveState((s) => ({ ...s, [template_key]: { error: extractApiError(e) } }));
    }
  }

  async function createPosting(payload) {
    const q = `?tenant_slug=${encodeURIComponent(slug)}`;
    const { data } = await api.post(`/hiring/postings${q}`, payload);
    setPostings((ps) => [...ps, data].sort((a, b) => a.slug.localeCompare(b.slug)));
    return data;
  }

  async function updatePosting(id, patch) {
    const q = `?tenant_slug=${encodeURIComponent(slug)}`;
    const { data } = await api.patch(`/hiring/postings/${id}${q}`, patch);
    setPostings((ps) => ps.map((p) => (p.id === id ? data : p)));
    return data;
  }

  async function deletePosting(id) {
    const q = `?tenant_slug=${encodeURIComponent(slug)}`;
    await api.delete(`/hiring/postings/${id}${q}`);
    setPostings((ps) => ps.filter((p) => p.id !== id));
  }

  return (
    <div className="space-y-6" data-testid="hiring-settings-page">
      <div className="flex items-center gap-3">
        <Users className="text-[#0e1420]" />
        <h1 className="text-2xl font-semibold text-[#0e1420]">Hiring — Settings</h1>
      </div>
      <p className="text-sm text-[#4b5563]">
        Role assignments, SLA windows, and message templates. Edits take effect immediately —
        no redeploy needed. Requires <code>platform_admin</code> or <code>owner</code> role to write.
      </p>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#e5e7eb]" data-testid="hiring-settings-tabs">
        {[
          ["roles", "Roles", ShieldAlert],
          ["postings", "Postings", Briefcase],
          ["sla", "SLA & Hours", Clock],
          ["templates", "Templates", Mail],
          ["calendar", "Calendar", Mail],
          ["email", "Email", Mail],
          ["callprep", "Call Prep", ShieldAlert],
        ].map(([k, label, Icon]) => (
          <button
            key={k}
            data-testid={`hiring-tab-${k}`}
            onClick={() => setTab(k)}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === k
                ? "border-[#0072bc] text-[#0072bc]"
                : "border-transparent text-[#4b5563] hover:text-[#0e1420]"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={load}
          data-testid="hiring-refresh-btn"
          className="inline-flex items-center gap-1 text-xs text-[#4b5563] hover:text-[#0e1420] px-2 self-center"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <PanelState
        status={state.loading ? "loading" : state.error ? "error" : "ok"}
        error={state.error}
        onRetry={load}
        testIdPrefix="hiring"
      />
      {!state.loading && !state.error && (
        <>
          {tab === "roles" && (
            <RolesTab
              roles={roles}
              saveState={saveState}
              canWrite={canWrite}
              onSave={saveRole}
            />
          )}
          {tab === "sla" && settings && (
            <SLATab
              settings={settings}
              saveState={saveState.settings || {}}
              canWrite={canWrite}
              onSave={saveSettings}
            />
          )}
          {tab === "templates" && (
            <TemplatesTab
              templates={templates}
              saveState={saveState}
              canWrite={canWrite}
              onSave={saveTemplate}
            />
          )}
          {tab === "calendar" && (
            <CalendarTab canWrite={canWrite} />
          )}
          {tab === "email" && settings && (
            <EmailTab settings={settings} saveState={saveState.settings || {}} canWrite={canWrite} onSave={saveSettings} />
          )}
          {tab === "callprep" && <CallPrepSettingsTab slug={slug} />}
          {tab === "postings" && (
            <PostingsTab
              postings={postings}
              tenantSlug={slug}
              canWrite={canWrite}
              onCreate={createPosting}
              onUpdate={updatePosting}
              onDelete={deletePosting}
            />
          )}
        </>
      )}
    </div>
  );
}

function RolesTab({ roles, saveState, canWrite, onSave }) {
  return (
    <div className="space-y-4" data-testid="hiring-roles-tab">
      {roles.map((role) => {
        const meta = ROLE_META[role.role_key] || { label: role.role_key, hint: "", maxPrimary: 1, spec: "" };
        const st = saveState[role.role_key] || {};
        return (
          <RoleCard
            key={role.role_key}
            role={role}
            meta={meta}
            saveState={st}
            canWrite={canWrite}
            onSave={(patch) => onSave(role.role_key, patch)}
          />
        );
      })}
    </div>
  );
}

function RoleCard({ role, meta, saveState, canWrite, onSave }) {
  const [primary, setPrimary] = useState((role.primary_user_emails || []).join(", "));
  const [backup, setBackup] = useState(role.backup_user_email || "");
  const [escalation, setEscalation] = useState((role.escalation_contact_emails || []).join(", "));
  const [localErr, setLocalErr] = useState(null);
  const isStage1 = role.role_key === "stage_1_approver";
  const isStage4 = role.role_key === "stage_4_interviewers";

  function submit() {
    setLocalErr(null);
    const primaryList = primary.split(",").map((s) => s.trim()).filter(Boolean);
    // Client-side gates that match the backend contract exactly, so a
    // known-bad submit never round-trips to a 400 (and used to blank
    // the page — pt.42, 2026-08-26).
    if (isStage4) {
      if (primaryList.length !== 2) {
        setLocalErr(
          "Stage 4 (Owner/COO) needs exactly two emails, comma-separated — " +
          "e.g. `ntoms@mrelectricmo.com, bpayne@mrelectricmo.com`.",
        );
        return;
      }
    } else if (primaryList.length !== 1) {
      setLocalErr(
        `${meta.label} is a solo-owner role — enter exactly one email in the ` +
        "primary field.",
      );
      return;
    }
    onSave({
      primary_user_emails: primaryList,
      backup_user_email: isStage4 ? null : (backup.trim() || null),
      escalation_contact_emails: isStage1
        ? escalation.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
    });
  }

  return (
    <div
      className="border border-[#e5e7eb] rounded-lg p-4 space-y-3"
      data-testid={`hiring-role-card-${role.role_key}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[#0e1420] font-semibold">{meta.label}</div>
          <div className="text-xs text-[#4b5563]">{meta.hint}</div>
          {meta.spec && (
            <div className="text-xs text-[#9ca3af] mt-0.5">Spec default: {meta.spec}</div>
          )}
        </div>
        {saveState.saved && <span className="text-xs text-[#16a34a]">Saved</span>}
        {saveState.saving && <Loader2 className="w-4 h-4 animate-spin text-[#4b5563]" />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-[#4b5563] block mb-1">
            {isStage4 ? "Primary interviewers (exactly 2 emails, comma-separated)" : "Primary owner (1 email)"}
          </label>
          <Input
            data-testid={`hiring-role-primary-${role.role_key}`}
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            disabled={!canWrite}
            placeholder="email@example.com"
          />
        </div>
        {!isStage4 && (
          <div>
            <label className="text-xs font-medium text-[#4b5563] block mb-1">
              Backup owner (Tier 2 reassignment target)
            </label>
            <Input
              data-testid={`hiring-role-backup-${role.role_key}`}
              value={backup}
              onChange={(e) => setBackup(e.target.value)}
              disabled={!canWrite}
              placeholder="backup@example.com"
            />
          </div>
        )}
        {isStage1 && (
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-[#4b5563] block mb-1">
              Escalation contacts (Tier 1 — Stage 1 only, comma-separated)
            </label>
            <Input
              data-testid={`hiring-role-escalation-${role.role_key}`}
              value={escalation}
              onChange={(e) => setEscalation(e.target.value)}
              disabled={!canWrite}
              placeholder="brett@..., nathanael@..."
            />
          </div>
        )}
      </div>

      {(saveState.error || localErr) && (
        <div className="text-xs text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-2" data-testid={`hiring-role-error-${role.role_key}`}>
          {localErr || saveState.error?.message || "Save failed."}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          data-testid={`hiring-role-save-${role.role_key}`}
          onClick={submit}
          disabled={!canWrite || saveState.saving}
        >
          <Save className="w-3.5 h-3.5 mr-1.5" />
          Save
        </Button>
      </div>
    </div>
  );
}

function SLATab({ settings, saveState, canWrite, onSave }) {
  const [sla, setSla] = useState(settings.sla);
  const [bh, setBh] = useState({
    tz: settings.business_hours_tz,
    start: settings.business_hours_start_local,
    end: settings.business_hours_end_local,
  });

  function submit() {
    onSave({
      sla: {
        stage_1_sla_hours: Number(sla.stage_1_sla_hours),
        stage_2_sla_hours: Number(sla.stage_2_sla_hours),
        stage_3_scheduling_sla_hours: Number(sla.stage_3_scheduling_sla_hours),
        stage_3_conducting_sla_business_days_min: Number(sla.stage_3_conducting_sla_business_days_min),
        stage_3_conducting_sla_business_days_max: Number(sla.stage_3_conducting_sla_business_days_max),
        stage_4_sla_business_days_min: Number(sla.stage_4_sla_business_days_min),
        stage_4_sla_business_days_max: Number(sla.stage_4_sla_business_days_max),
        tier_2_reassignment_hours: Number(sla.tier_2_reassignment_hours),
        tier_1_escalation_enabled_for_stage_1: !!sla.tier_1_escalation_enabled_for_stage_1,
      },
      business_hours_tz: bh.tz,
      business_hours_start_local: bh.start,
      business_hours_end_local: bh.end,
    });
  }

  const fields = [
    ["stage_1_sla_hours",                       "Stage 1 SLA (hours)",                        "number"],
    ["stage_2_sla_hours",                       "Stage 2 SLA (business hours)",               "number"],
    ["stage_3_scheduling_sla_hours",            "Stage 3 scheduling SLA (hours)",             "number"],
    ["stage_3_conducting_sla_business_days_min","Stage 3 conducting SLA — min business days", "number"],
    ["stage_3_conducting_sla_business_days_max","Stage 3 conducting SLA — max business days", "number"],
    ["stage_4_sla_business_days_min",           "Stage 4 SLA — min business days",            "number"],
    ["stage_4_sla_business_days_max",           "Stage 4 SLA — max business days",            "number"],
    ["tier_2_reassignment_hours",               "Tier 2 reassignment window (hours after SLA breach)", "number"],
  ];

  return (
    <div className="space-y-6" data-testid="hiring-sla-tab">
      <div>
        <h2 className="text-base font-semibold text-[#0e1420] mb-2">Business hours</h2>
        <p className="text-xs text-[#4b5563] mb-3">
          SLA clocks tick only during these hours. If a stage entry happens outside
          business hours, the clock starts at the next business-hour boundary.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-[#4b5563] block mb-1">Timezone (IANA)</label>
            <Input data-testid="hiring-bh-tz" value={bh.tz} onChange={(e) => setBh((b) => ({ ...b, tz: e.target.value }))} disabled={!canWrite} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#4b5563] block mb-1">Start (HH:MM local)</label>
            <Input data-testid="hiring-bh-start" value={bh.start} onChange={(e) => setBh((b) => ({ ...b, start: e.target.value }))} disabled={!canWrite} placeholder="09:00" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#4b5563] block mb-1">End (HH:MM local)</label>
            <Input data-testid="hiring-bh-end" value={bh.end} onChange={(e) => setBh((b) => ({ ...b, end: e.target.value }))} disabled={!canWrite} placeholder="17:00" />
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-base font-semibold text-[#0e1420] mb-2">Stage SLAs & escalation</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fields.map(([key, label, type]) => (
            <div key={key}>
              <label className="text-xs font-medium text-[#4b5563] block mb-1">{label}</label>
              <Input
                data-testid={`hiring-sla-${key}`}
                type={type}
                value={sla[key]}
                onChange={(e) => setSla((s) => ({ ...s, [key]: e.target.value }))}
                disabled={!canWrite}
              />
            </div>
          ))}
          <label className="flex items-center gap-2 text-sm text-[#0e1420] mt-6">
            <input
              type="checkbox"
              data-testid="hiring-sla-tier1-enabled"
              checked={!!sla.tier_1_escalation_enabled_for_stage_1}
              onChange={(e) => setSla((s) => ({ ...s, tier_1_escalation_enabled_for_stage_1: e.target.checked }))}
              disabled={!canWrite}
            />
            Enable Tier 1 escalation on Stage 1 (notify escalation contacts before Tier 2 reassignment)
          </label>
        </div>
      </div>

      {saveState.error && (
        <div className="text-xs text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-2" data-testid="hiring-sla-error">
          {saveState.error.message || "Save failed."}
        </div>
      )}
      {saveState.saved && (
        <div className="text-xs text-[#16a34a]" data-testid="hiring-sla-saved">Saved.</div>
      )}

      <div className="flex justify-end">
        <Button data-testid="hiring-sla-save" onClick={submit} disabled={!canWrite || saveState.saving}>
          {saveState.saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Save
        </Button>
      </div>
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// CC-300 + CC-299: candidate Reply-To and inbound routing addresses.
function EmailTab({ settings, saveState, canWrite, onSave }) {
  const [replyTo, setReplyTo] = useState(settings.candidate_reply_to || "");
  const [inbound, setInbound] = useState((settings.inbound_addresses || []).join("\n"));
  const [err, setErr] = useState("");
  function submit() {
    const r = replyTo.trim();
    const list = inbound.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
    const bad = [r, ...list].filter((x) => x && !EMAIL_RE.test(x));
    if (bad.length) { setErr(`Not a valid email address: ${bad.join(", ")}`); return; }
    setErr("");
    onSave({ candidate_reply_to: r, inbound_addresses: list });
  }
  return (
    <div className="space-y-6 max-w-xl" data-testid="hiring-email-tab">
      <div>
        <h2 className="text-base font-semibold text-[#0e1420] mb-1">Candidate reply-to address</h2>
        <p className="text-xs text-[#4b5563] mb-2">
          Replies to candidate emails (intake link, declines, offers) go here. Staff notifications are not affected.
          Leave empty to send without a Reply-To.
        </p>
        <p className="text-xs text-[#4b5563] mb-2" data-testid="hiring-reply-to-current">
          Current: <span className="font-mono">{settings.candidate_reply_to || "none (no Reply-To)"}</span>
        </p>
        <Input data-testid="hiring-reply-to-input" type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} disabled={!canWrite} placeholder="name@example.com" />
      </div>
      <div>
        <h2 className="text-base font-semibold text-[#0e1420] mb-1">Inbound hiring addresses</h2>
        <p className="text-xs text-[#4b5563] mb-2">
          Applicant emails sent to these addresses are filed under this company. One per line.
        </p>
        <textarea data-testid="hiring-inbound-addresses-input" rows={3} value={inbound} onChange={(e) => setInbound(e.target.value)} disabled={!canWrite}
          className="w-full rounded-md border border-[#d1d5db] px-3 py-2 text-sm font-mono" />
      </div>
      {(err || saveState.error) && (
        <div className="text-xs text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-2" data-testid="hiring-email-error">
          {err || saveState.error.message || "Save failed."}
        </div>
      )}
      {saveState.saved && <div className="text-xs text-[#16a34a]" data-testid="hiring-email-saved">Saved.</div>}
      <div className="flex justify-end">
        <Button data-testid="hiring-email-save" onClick={submit} disabled={!canWrite || saveState.saving}>
          {saveState.saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Save
        </Button>
      </div>
    </div>
  );
}

function TemplatesTab({ templates, saveState, canWrite, onSave }) {
  return (
    <div className="space-y-4" data-testid="hiring-templates-tab">
      <p className="text-xs text-[#4b5563]">
        Placeholders: <code>{"{candidate_name}"}</code>, <code>{"{candidate_first_name}"}</code>,{" "}
        <code>{"{stage_owner_name}"}</code>, <code>{"{stage_owner_email}"}</code>,{" "}
        <code>{"{intake_link}"}</code>, <code>{"{position_title}"}</code>, <code>{"{company_name}"}</code>,{" "}
        <code>{"{decline_reason}"}</code>. Unknown placeholders render literally so drafts don&apos;t break sends.
      </p>
      {templates.map((tpl) => (
        <TemplateEditor
          key={tpl.template_key}
          tpl={tpl}
          meta={TEMPLATE_META[tpl.template_key] || { label: tpl.template_key }}
          saveState={saveState[tpl.template_key] || {}}
          canWrite={canWrite}
          onSave={(patch) => onSave(tpl.template_key, patch)}
        />
      ))}
    </div>
  );
}

function TemplateEditor({ tpl, meta, saveState, canWrite, onSave }) {
  const [subject, setSubject] = useState(tpl.subject);
  const [body, setBody] = useState(tpl.body_text);
  const dirty = subject !== tpl.subject || body !== tpl.body_text;

  return (
    <div className="border border-[#e5e7eb] rounded-lg p-4" data-testid={`hiring-template-card-${tpl.template_key}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[#0e1420] font-semibold text-sm">{meta.label}</div>
        {saveState.saved && <span className="text-xs text-[#16a34a]">Saved</span>}
        {saveState.saving && <Loader2 className="w-4 h-4 animate-spin text-[#4b5563]" />}
      </div>
      <label className="text-xs font-medium text-[#4b5563] block mb-1">Subject</label>
      <Input
        data-testid={`hiring-template-subject-${tpl.template_key}`}
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        disabled={!canWrite}
      />
      <label className="text-xs font-medium text-[#4b5563] block mb-1 mt-3">Body</label>
      <Textarea
        data-testid={`hiring-template-body-${tpl.template_key}`}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={6}
        disabled={!canWrite}
      />
      {saveState.error && (
        <div className="text-xs text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-2 mt-2">
          {saveState.error.message || "Save failed."}
        </div>
      )}
      <div className="flex justify-end mt-3">
        <Button
          data-testid={`hiring-template-save-${tpl.template_key}`}
          onClick={() => onSave({ subject, body_text: body })}
          disabled={!canWrite || !dirty || saveState.saving}
        >
          <Save className="w-3.5 h-3.5 mr-1.5" />
          Save
        </Button>
      </div>
    </div>
  );
}

// Phase F, pt.26 — Google Calendar connect card.
function CalendarTab({ canWrite }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/hiring/calendar/status");
      setStatus(data);
      setErr(null);
    } catch (e) {
      setErr(extractApiError(e));
    }
  }
  useEffect(() => { load(); }, []);

  async function connect() {
    setBusy(true);
    try {
      const { data } = await api.get("/hiring/calendar/oauth/start");
      window.location.href = data.authorization_url;
    } catch (e) {
      setErr(extractApiError(e));
      setBusy(false);
    }
  }
  async function disconnect() {
    if (!window.confirm("Disconnect Google Calendar? Existing scheduled interviews stay in Google but new ones cannot be created until reconnected.")) return;
    setBusy(true);
    try {
      await api.delete("/hiring/calendar/disconnect");
      await load();
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!status) return <div className="text-sm text-[#4b5563]">Loading…</div>;
  return (
    <div className="bg-white rounded-lg border border-[#e5e7eb] p-5 space-y-4" data-testid="hiring-calendar-connect-card">
      <div>
        <h3 className="text-lg font-semibold text-[#0e1420]">Google Calendar</h3>
        <p className="text-sm text-[#4b5563] mt-1">
          Stage 4 interviews are written to the shared hiring calendar. Requires a one-time OAuth consent.
        </p>
      </div>
      {!status.enabled && (
        <div className="bg-[#fef3c7] border border-[#fbbf24] rounded p-3 text-xs text-[#78350f]">
          Integration flag <code>HIRING_CALENDAR_ENABLED</code> is <b>false</b>. Even if connected, event creation will refuse until an admin flips the flag.
        </div>
      )}
      {status.connected ? (
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm">
            <div className="text-[#166534]">✓ Connected as <b>{status.connected_email}</b></div>
            <div className="text-xs text-[#4b5563] mt-1">
              Since {new Date(status.connected_at).toLocaleString()} · Calendar ID: <code className="text-xs">{status.calendar_id}</code>
            </div>
          </div>
          {canWrite && (
            <Button variant="outline" size="sm" onClick={disconnect} disabled={busy} data-testid="hiring-calendar-disconnect-btn">
              Disconnect
            </Button>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-[#4b5563]">Not connected yet.</div>
          {canWrite && (
            <Button size="sm" onClick={connect} disabled={busy} data-testid="hiring-calendar-connect-btn">
              {busy ? "Redirecting…" : "Connect Google Calendar"}
            </Button>
          )}
        </div>
      )}
      {err && <div className="text-xs text-[#991b1b]">{err?.message || err}</div>}
    </div>
  );
}


// ============================================================================
// Postings Tab — pt.41 (2026-08-26).
// Admin panel for public /apply/:tenantSlug/:postingSlug URLs.
// Create + toggle-active + copy-link + delete (with audit-ref guard).
// Free-text title + auto-slugified editable slug + curated location
// dropdown w/ Custom fallback. No board syndication — single-URL only.
// ============================================================================

const PROD_HOST = "https://currentcontrolhq.com";
const LOCATION_OPTIONS = [
  "Springfield, MO",
  "Joplin, MO",
  "Central MO",
];
const CUSTOM_LOCATION = "__custom__";

function slugifyTitle(title) {
  return (title || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 64);
}

function PostingsTab({ postings, tenantSlug, canWrite, onCreate, onUpdate, onDelete }) {
  const sorted = useMemo(
    () => [...postings].sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      return a.slug.localeCompare(b.slug);
    }),
    [postings],
  );

  return (
    <div className="space-y-4" data-testid="hiring-postings-tab">
      <div className="text-[13px] text-[#4b5563]">
        Public application URLs you paste into Indeed, ZipRecruiter, flyers, or QR codes.
        Each posting has its own <code>/apply/{tenantSlug}/&#123;slug&#125;</code> URL —
        toggle inactive to fail-closed on the public page.
      </div>
      {canWrite && (
        <NewPostingForm tenantSlug={tenantSlug} onCreate={onCreate} />
      )}
      {sorted.length === 0 ? (
        <div className="text-sm italic text-[#4b5563]" data-testid="hiring-postings-empty">
          No postings yet — create your first one above.
        </div>
      ) : (
        <div className="space-y-2" data-testid="hiring-postings-list">
          {sorted.map((p) => (
            <PostingRow
              key={p.id}
              posting={p}
              tenantSlug={tenantSlug}
              canWrite={canWrite}
              onUpdate={onUpdate}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NewPostingForm({ tenantSlug, onCreate }) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [locationChoice, setLocationChoice] = useState(LOCATION_OPTIONS[0]);
  const [customLocation, setCustomLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  function handleTitleChange(v) {
    setTitle(v);
    if (!slugTouched) setSlug(slugifyTitle(v));
  }

  const effectiveLocation =
    locationChoice === CUSTOM_LOCATION ? customLocation.trim() : locationChoice;
  const canSubmit = title.trim() && slug.trim() && !busy;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await onCreate({
        slug: slug.trim(),
        position_title: title.trim(),
        location: effectiveLocation,
      });
      setTitle("");
      setSlug("");
      setSlugTouched(false);
      setLocationChoice(LOCATION_OPTIONS[0]);
      setCustomLocation("");
    } catch (e2) {
      setErr(extractApiError(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="bg-[#f9fafb] border border-dashed border-[#cbd5e1] rounded-lg p-4 space-y-3"
      data-testid="hiring-postings-new-form"
    >
      <div className="flex items-center gap-2 text-[13px] font-semibold text-[#0e1420]">
        <Plus className="w-4 h-4" /> New posting
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="block text-[10.5px] uppercase tracking-widest text-[#4b5563] mb-1">
            Title
          </label>
          <Input
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Electrician"
            data-testid="hiring-postings-new-title"
          />
        </div>
        <div>
          <label className="block text-[10.5px] uppercase tracking-widest text-[#4b5563] mb-1">
            Slug <span className="text-[#4b5563] normal-case tracking-normal font-normal">(auto — editable)</span>
          </label>
          <Input
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"));
              setSlugTouched(true);
            }}
            placeholder="electrician-springfield"
            data-testid="hiring-postings-new-slug"
          />
        </div>
        <div>
          <label className="block text-[10.5px] uppercase tracking-widest text-[#4b5563] mb-1">
            Location
          </label>
          <select
            value={locationChoice}
            onChange={(e) => setLocationChoice(e.target.value)}
            className="w-full h-10 rounded-md border border-[#e5e7eb] bg-white px-3 text-sm"
            data-testid="hiring-postings-new-location"
          >
            {LOCATION_OPTIONS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
            <option value={CUSTOM_LOCATION}>Custom…</option>
          </select>
          {locationChoice === CUSTOM_LOCATION && (
            <Input
              value={customLocation}
              onChange={(e) => setCustomLocation(e.target.value)}
              placeholder="e.g. Branson, MO"
              className="mt-2"
              data-testid="hiring-postings-new-location-custom"
            />
          )}
        </div>
        <div className="flex items-end">
          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full"
            data-testid="hiring-postings-new-submit"
          >
            {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</>) : "Create posting"}
          </Button>
        </div>
      </div>
      {slug && (
        <div className="text-[11px] text-[#4b5563] font-mono">
          Will resolve to: <span className="text-[#0072bc]">{PROD_HOST}/apply/{tenantSlug}/{slug}</span>
        </div>
      )}
      {err && (
        <div
          className="text-[12px] text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-2"
          data-testid="hiring-postings-new-error"
        >
          {err?.message || err}
        </div>
      )}
    </form>
  );
}

function PostingRow({ posting, tenantSlug, canWrite, onUpdate, onDelete }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(posting.position_title);
  const [editLocation, setEditLocation] = useState(posting.location || "");
  const [confirmDel, setConfirmDel] = useState(false);
  const [posterBusy, setPosterBusy] = useState(false);

  const url = `${PROD_HOST}/apply/${tenantSlug}/${posting.slug}`;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setErr("Copy failed — select the URL manually and copy it.");
    }
  }

  async function downloadPoster() {
    setPosterBusy(true);
    setErr(null);
    try {
      const q = `?tenant_slug=${encodeURIComponent(tenantSlug)}`;
      const resp = await api.get(`/hiring/postings/${posting.id}/poster.pdf${q}`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `apply-${posting.slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setPosterBusy(false);
    }
  }

  async function toggleActive() {
    setBusy(true);
    setErr(null);
    try {
      await onUpdate(posting.id, { active: !posting.active });
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveEdits() {
    setBusy(true);
    setErr(null);
    try {
      const patch = {};
      if (editTitle.trim() !== posting.position_title) patch.position_title = editTitle.trim();
      if (editLocation.trim() !== (posting.location || "")) patch.location = editLocation.trim();
      if (Object.keys(patch).length) {
        await onUpdate(posting.id, patch);
      }
      setEditing(false);
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setBusy(true);
    setErr(null);
    try {
      await onDelete(posting.id);
    } catch (e) {
      setErr(extractApiError(e));
      setConfirmDel(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={`bg-white border rounded-lg p-4 space-y-3 transition ${
        posting.active
          ? "border-[#e5e7eb]"
          : "border-[#e5e7eb] bg-[#f9fafb] opacity-70"
      }`}
      data-testid={`hiring-posting-row-${posting.slug}`}
      data-active={posting.active ? "true" : "false"}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-2">
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                data-testid={`hiring-posting-edit-title-${posting.slug}`}
              />
              <Input
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
                placeholder="Location"
                data-testid={`hiring-posting-edit-location-${posting.slug}`}
              />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-[15px] text-[#0e1420]">
                  {posting.position_title}
                </div>
                <span
                  className={`text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${
                    posting.active
                      ? "bg-[#dcfce7] text-[#0e8a68]"
                      : "bg-[#e5e7eb] text-[#4b5563]"
                  }`}
                  data-testid={`hiring-posting-status-${posting.slug}`}
                >
                  {posting.active ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="text-[12px] text-[#4b5563] mt-0.5">
                {posting.location || <span className="italic">No location set</span>}
                {" · "}
                <span className="font-mono">{posting.slug}</span>
              </div>
            </>
          )}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <code
              className="text-[11px] font-mono bg-[#f5f6f8] border border-[#e5e7eb] rounded px-2 py-1 text-[#0072bc] break-all"
              data-testid={`hiring-posting-url-${posting.slug}`}
            >
              {url}
            </code>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={copyLink}
            className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1.5 rounded border border-[#e5e7eb] hover:bg-[#f5f6f8] text-[#0e1420]"
            data-testid={`hiring-posting-copy-${posting.slug}`}
            title="Copy the /apply/ URL to paste into Indeed / ZipRecruiter / flyers"
          >
            <Link2 className="w-3.5 h-3.5" />
            {copied ? "Copied" : "Copy link"}
          </button>
          <button
            type="button"
            onClick={downloadPoster}
            disabled={posterBusy}
            className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1.5 rounded border border-[#c99a3f] text-[#7a5a1c] hover:bg-[#fdf6e6] disabled:opacity-60"
            data-testid={`hiring-posting-poster-${posting.slug}`}
            title="Download a printable QR poster (PDF, letter size, ready to hang)"
          >
            {posterBusy ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FileDown className="w-3.5 h-3.5" />
            )}
            Poster
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={toggleActive}
              disabled={busy}
              role="switch"
              aria-checked={posting.active}
              className={`relative w-11 h-6 rounded-full transition disabled:opacity-60 ${
                posting.active ? "bg-[#0e8a68]" : "bg-[#cbd5e1]"
              }`}
              data-testid={`hiring-posting-toggle-${posting.slug}`}
              data-enabled={posting.active ? "true" : "false"}
              title={posting.active ? "Deactivate — public URL will 404" : "Reactivate"}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                posting.active ? "translate-x-5" : ""
              }`} />
            </button>
          )}
          {canWrite && !editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-[11.5px] font-semibold px-2 py-1.5 rounded border border-[#e5e7eb] hover:bg-[#f5f6f8] text-[#0e1420]"
              data-testid={`hiring-posting-edit-${posting.slug}`}
            >
              Edit
            </button>
          )}
          {canWrite && editing && (
            <>
              <button
                type="button"
                onClick={saveEdits}
                disabled={busy}
                className="text-[11.5px] font-semibold px-2 py-1.5 rounded bg-[#0072bc] text-white hover:bg-[#005a95]"
                data-testid={`hiring-posting-save-${posting.slug}`}
              >
                {busy ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setEditTitle(posting.position_title);
                  setEditLocation(posting.location || "");
                }}
                className="text-[11.5px] font-semibold px-2 py-1.5 rounded border border-[#e5e7eb] hover:bg-[#f5f6f8] text-[#0e1420]"
                data-testid={`hiring-posting-cancel-${posting.slug}`}
              >
                Cancel
              </button>
            </>
          )}
          {canWrite && (
            <button
              type="button"
              onClick={() => setConfirmDel(true)}
              disabled={busy}
              className="p-1.5 rounded border border-[#e5e7eb] hover:bg-[#fee2e2] text-[#7a1c1c]"
              data-testid={`hiring-posting-delete-${posting.slug}`}
              title="Delete (only if no applicants have landed on this URL)"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      {confirmDel && (
        <div
          className="bg-[#fef3c7] border border-[#fcd34d] rounded p-3 text-[12px] text-[#78350f] flex items-center justify-between gap-3"
          data-testid={`hiring-posting-confirm-delete-${posting.slug}`}
        >
          <div>
            Delete <b>{posting.slug}</b>? This fails if any real applicant has ever landed on it —
            use the toggle to deactivate instead if you&apos;re not sure.
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={doDelete}
              disabled={busy}
              className="text-[11.5px] font-semibold px-2 py-1 rounded bg-[#7a1c1c] text-white hover:bg-[#5c1414]"
              data-testid={`hiring-posting-confirm-yes-${posting.slug}`}
            >
              {busy ? "Deleting…" : "Delete"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDel(false)}
              className="text-[11.5px] font-semibold px-2 py-1 rounded border border-[#e5e7eb] hover:bg-white text-[#0e1420]"
              data-testid={`hiring-posting-confirm-no-${posting.slug}`}
            >
              Keep
            </button>
          </div>
        </div>
      )}
      {err && (
        <div
          className="text-[12px] text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-2"
          data-testid={`hiring-posting-error-${posting.slug}`}
        >
          {err?.message || err}
        </div>
      )}
    </div>
  );
}
